import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Button,
  Text,
  Badge,
  Alert,
  AlertIcon,
  useToast,
  useColorModeValue,
  Icon,
  Divider,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Spinner,
  Center,
  Code,
} from '@chakra-ui/react';
import { FiVideo, FiCheckCircle, FiSettings } from 'react-icons/fi';
import { SiGooglemeet, SiZoom } from 'react-icons/si';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { db } from '../../../utils/init-firebase';

export default function MeetingProviderSettings({ userId }) {
  const toast = useToast();
  const [userData, setUserData] = useState(null);
  const [preferredProvider, setPreferredProvider] = useState('google-meet');
  const [zoomPMI, setZoomPMI] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const cardBG = useColorModeValue('gray.50', 'gray.700');

  useEffect(() => {
    if (!userId) return;

    const userRef = doc(db, 'users', userId);
    const unsubscribe = onSnapshot(userRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setUserData(data);
        setPreferredProvider(data.meetingProviderSettings?.preferredProvider || 'google-meet');
        setZoomPMI(data.meetingProviderSettings?.zoomPMI || '');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [userId]);

  const handleSaveSettings = async () => {
    setIsSaving(true);
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        'meetingProviderSettings.preferredProvider': preferredProvider,
        'meetingProviderSettings.zoomPMI': zoomPMI.trim(),
      });

      toast({
        title: 'Settings saved',
        description: 'Meeting provider preferences updated',
        status: 'success',
        duration: 2000,
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: 'Error saving settings',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <Center py={8}>
        <Spinner />
      </Center>
    );
  }

  const hasCalendarConnected = userData?.googleCalendarConnected === true;

  return (
    <VStack align="stretch" spacing={6}>
      <Box>
        <Heading size="md" mb={2}>Meeting Provider</Heading>
        <Text fontSize="sm" color="gray.600">
          Choose your preferred video conferencing platform for meetings
        </Text>
      </Box>

      <Divider />

      {!hasCalendarConnected && (
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          <VStack align="flex-start" spacing={1} flex={1}>
            <Text fontSize="sm" fontWeight="semibold">
              Google Calendar Required
            </Text>
            <Text fontSize="xs">
              Connect your Google Calendar in the Calendar Sync tab before configuring meeting providers.
            </Text>
          </VStack>
        </Alert>
      )}

      {/* Provider Selection */}
      <FormControl>
        <FormLabel fontSize="sm" fontWeight="semibold" mb={3}>
          Preferred Video Platform
        </FormLabel>
        <RadioGroup 
          value={preferredProvider} 
          onChange={setPreferredProvider}
          isDisabled={!hasCalendarConnected}
        >
          <VStack align="stretch" spacing={3}>
            {/* Google Meet */}
            <Box
              p={4}
              bg={preferredProvider === 'google-meet' ? 'blue.50' : cardBG}
              borderWidth="2px"
              borderColor={preferredProvider === 'google-meet' ? 'blue.400' : borderColor}
              borderRadius="lg"
              cursor="pointer"
              onClick={() => hasCalendarConnected && setPreferredProvider('google-meet')}
              transition="all 0.2s"
              _hover={hasCalendarConnected ? { borderColor: 'blue.300', shadow: 'sm' } : {}}
            >
              <HStack spacing={3}>
                <Radio value="google-meet" colorScheme="blue" />
                <Icon as={SiGooglemeet} boxSize={6} color="blue.500" />
                <VStack align="flex-start" spacing={0} flex={1}>
                  <Text fontWeight="semibold" fontSize="sm">Google Meet</Text>
                  <Text fontSize="xs" color="gray.600">
                    Automatically generates Google Meet links for calendar events
                  </Text>
                </VStack>
                {preferredProvider === 'google-meet' && (
                  <Badge colorScheme="blue">Selected</Badge>
                )}
              </HStack>
            </Box>

            {/* Zoom */}
            <Box
              p={4}
              bg={preferredProvider === 'zoom' ? 'blue.50' : cardBG}
              borderWidth="2px"
              borderColor={preferredProvider === 'zoom' ? 'blue.400' : borderColor}
              borderRadius="lg"
              cursor="pointer"
              onClick={() => hasCalendarConnected && setPreferredProvider('zoom')}
              transition="all 0.2s"
              _hover={hasCalendarConnected ? { borderColor: 'blue.300', shadow: 'sm' } : {}}
            >
              <HStack spacing={3}>
                <Radio value="zoom" colorScheme="blue" />
                <Icon as={SiZoom} boxSize={6} color="blue.600" />
                <VStack align="flex-start" spacing={0} flex={1}>
                  <Text fontWeight="semibold" fontSize="sm">Zoom</Text>
                  <Text fontSize="xs" color="gray.600">
                    Adds your personal Zoom meeting link to events
                  </Text>
                </VStack>
                {preferredProvider === 'zoom' && (
                  <Badge colorScheme="blue">Selected</Badge>
                )}
              </HStack>
            </Box>

            {/* None */}
            <Box
              p={4}
              bg={preferredProvider === 'none' ? 'blue.50' : cardBG}
              borderWidth="2px"
              borderColor={preferredProvider === 'none' ? 'blue.400' : borderColor}
              borderRadius="lg"
              cursor="pointer"
              onClick={() => hasCalendarConnected && setPreferredProvider('none')}
              transition="all 0.2s"
              _hover={hasCalendarConnected ? { borderColor: 'blue.300', shadow: 'sm' } : {}}
            >
              <HStack spacing={3}>
                <Radio value="none" colorScheme="blue" />
                <Icon as={FiVideo} boxSize={6} color="gray.500" />
                <VStack align="flex-start" spacing={0} flex={1}>
                  <Text fontWeight="semibold" fontSize="sm">No Video Link</Text>
                  <Text fontSize="xs" color="gray.600">
                    Calendar events won't include video meeting links
                  </Text>
                </VStack>
                {preferredProvider === 'none' && (
                  <Badge colorScheme="gray">Selected</Badge>
                )}
              </HStack>
            </Box>
          </VStack>
        </RadioGroup>
      </FormControl>

      {/* Zoom PMI Configuration */}
      {preferredProvider === 'zoom' && (
        <Box p={4} bg={cardBG} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
          <FormControl>
            <FormLabel fontSize="sm" fontWeight="semibold" mb={2}>
              Zoom Personal Meeting ID (PMI)
            </FormLabel>
            <Text fontSize="xs" color="gray.600" mb={3}>
              Enter your Zoom PMI or personal meeting link. This will be added to all scheduled meetings.
            </Text>
            <Input
              placeholder="https://zoom.us/j/1234567890 or 1234567890"
              value={zoomPMI}
              onChange={(e) => setZoomPMI(e.target.value)}
              size="sm"
            />
            <Text fontSize="xs" color="gray.500" mt={2}>
              Example: <Code fontSize="xs">https://zoom.us/j/1234567890</Code>
            </Text>
          </FormControl>
        </Box>
      )}

      {/* Google Meet Info */}
      {preferredProvider === 'google-meet' && (
        <Alert status="success" borderRadius="md">
          <AlertIcon />
          <VStack align="flex-start" spacing={1} flex={1}>
            <Text fontSize="sm" fontWeight="semibold">
              Google Meet Ready
            </Text>
            <Text fontSize="xs">
              Google Meet links will be automatically generated when you schedule meetings.
              This requires your Google Calendar to be connected.
            </Text>
          </VStack>
        </Alert>
      )}

      {/* Save Button */}
      <Button
        colorScheme="blue"
        onClick={handleSaveSettings}
        isLoading={isSaving}
        loadingText="Saving..."
        isDisabled={!hasCalendarConnected}
        leftIcon={<FiCheckCircle />}
      >
        Save Preferences
      </Button>
    </VStack>
  );
}

